import { cn } from "@/lib/utils"
import { type LucideIcon } from "lucide-react"

export type McPillTone =
  | "default"
  | "muted"
  | "attention"
  | "doubt"
  | "success"
  | "danger"
  | "info"

const toneClasses: Record<McPillTone, string> = {
  default: "bg-grey4 text-white border-transparent",
  muted: "bg-transparent text-grey5 border-grey4",
  attention: "bg-attention/10 text-attention border-transparent",
  doubt: "bg-doubt/10 text-doubt border-transparent",
  success: "bg-success/10 text-success border-transparent",
  danger: "bg-red1/10 text-red1 border-transparent",
  info: "bg-grey5/10 text-grey5 border-grey4",
}

interface McPillProps {
  tone?: McPillTone
  icon?: LucideIcon
  dim?: boolean
  className?: string
  /** Tooltip; defaults to a plain-string label itself (e.g. a short label's full name). */
  title?: string
  children: React.ReactNode
}

export function McPill({
  tone = "default",
  icon: Icon,
  dim,
  className,
  title,
  children,
}: McPillProps) {
  return (
    <span
      title={title ?? (typeof children === "string" ? children : undefined)}
      className={cn(
        "inline-flex h-[22px] max-w-full items-center gap-1 whitespace-nowrap rounded-full border px-2 text-[11.5px] font-medium leading-none tracking-[0.01em]",
        toneClasses[tone],
        dim && "opacity-60",
        className,
      )}
    >
      {Icon && <Icon size={11} className="shrink-0" />}
      {/* Wrap only plain-string labels for ellipsis truncation — element
          children (e.g. an inline spinner + label) must stay direct flex
          children so the pill's gap keeps spacing them. */}
      {/* `leading-tight`, not the pill's `leading-none`: `truncate` sets
          overflow hidden, and at line-height 1 the box is too short for
          descenders, so the "g" in "Nachgelagert" lost its tail. */}
      {typeof children === "string" ? (
        <span className="min-w-0 truncate leading-tight">{children}</span>
      ) : (
        children
      )}
    </span>
  )
}
